import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import type { components } from '../api/schema';
import { unwrap } from '../api/unwrap';
import { buildListQuerySerializer } from '../lib/list-query';
import { pruneUndefined } from '../lib/prune';

import type {
  CampaignCreateInput,
  CampaignItemSelectionMode,
  CampaignItemStatus,
  CampaignStatus,
  CampaignUpdateInput,
} from './schemas';

type CreateCampaignRequest = components['schemas']['CreateCampaignRequest'];
type UpdateCampaignRequest = components['schemas']['UpdateCampaignRequest'];

export const campaignsKeys = {
  all: ['campaigns'] as const,
  list: (input: CampaignsListInput) => ['campaigns', 'list', input] as const,
  one: (id: string) => ['campaigns', 'one', id] as const,
  items: (id: string) => ['campaigns', 'one', id, 'items'] as const,
};

export interface CampaignRecord {
  id: string;
  clientId: string;
  warehouseId: string;
  name: string;
  messageText: string;
  itemSelectionMode: CampaignItemSelectionMode;
  status: CampaignStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CampaignItemRecord {
  id: string;
  campaignId: string;
  marketHashName: string;
  assetId: string | null;
  sourceAccountId: string | null;
  appid: number;
  contextId: number;
  quantity: number;
  priority: number;
  itemPrice: string | null;
  status: CampaignItemStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CampaignItemInput {
  marketHashName: string;
  appid: number;
  contextId?: number;
  quantity?: number;
  priority?: number;
  assetId?: string | null;
  sourceAccountId?: string | null;
}

// Phase 8.5-05 — server-side URL-driven list (ADR-014). Bracketed filter
// + signed sort + 1-indexed page; backend returns
// `{ items, page, pageSize, total }`. Hook surfaces the full envelope —
// list screens render `total` in pagination footer.
export type CampaignSort = 'createdAt' | 'name' | 'launchedAt';

export interface CampaignsListFilter {
  clientId?: string | undefined;
  status?: CampaignStatus | undefined;
  includeDeleted?: boolean | undefined;
}

export interface CampaignsListInput {
  filter: CampaignsListFilter;
  sort: { field: CampaignSort | null; dir: 'asc' | 'desc' };
  page: number;
  pageSize: number;
}

export interface CampaignsListResult {
  items: CampaignRecord[];
  page: number;
  pageSize: number;
  total: number;
}

const formatSort = (sort: CampaignsListInput['sort']): string => {
  const field = sort.field ?? 'createdAt';
  return sort.dir === 'desc' ? `-${field}` : field;
};

export const useCampaigns = (input: CampaignsListInput) =>
  useQuery({
    queryKey: campaignsKeys.list(input),
    queryFn: async (): Promise<CampaignsListResult> => {
      const filter: { clientId?: string; status?: CampaignStatus; includeDeleted?: 'true' } = {};
      if (input.filter.clientId) filter.clientId = input.filter.clientId;
      if (input.filter.status) filter.status = input.filter.status;
      if (input.filter.includeDeleted) filter.includeDeleted = 'true';
      const result = await api.GET('/api/campaigns', {
        params: {
          query: {
            sort: formatSort(input.sort),
            page: String(input.page),
            pageSize: String(input.pageSize),
          },
        },
        querySerializer: buildListQuerySerializer(filter),
      });
      return unwrap(result) as CampaignsListResult;
    },
  });

export const useCampaign = (id: string | undefined) =>
  useQuery({
    queryKey: id ? campaignsKeys.one(id) : ['campaigns', 'one', 'pending'],
    enabled: Boolean(id),
    queryFn: async (): Promise<CampaignRecord> => {
      if (!id) throw new Error('campaign id required');
      const result = await api.GET('/api/campaigns/{id}', { params: { path: { id } } });
      return unwrap(result).campaign;
    },
  });

export const useCampaignItems = (id: string | undefined) =>
  useQuery({
    queryKey: id ? campaignsKeys.items(id) : ['campaigns', 'pending', 'items'],
    enabled: Boolean(id),
    queryFn: async (): Promise<CampaignItemRecord[]> => {
      if (!id) throw new Error('campaign id required');
      const result = await api.GET('/api/campaigns/{id}/items', { params: { path: { id } } });
      return unwrap(result).items;
    },
  });

const invalidateCampaigns = (qc: ReturnType<typeof useQueryClient>): Promise<void> =>
  qc.invalidateQueries({ queryKey: campaignsKeys.all });

export const useCreateCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CampaignCreateInput): Promise<CampaignRecord> => {
      const { clientId, ...rest } = body;
      const payload = pruneUndefined(rest) as CreateCampaignRequest;
      const result = await api.POST('/api/clients/{clientId}/campaigns', {
        params: { path: { clientId } },
        body: payload,
      });
      return unwrap(result).campaign;
    },
    onSuccess: () => invalidateCampaigns(qc),
  });
};

export const useUpdateCampaign = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CampaignUpdateInput): Promise<CampaignRecord> => {
      const payload = pruneUndefined(body) as UpdateCampaignRequest;
      const result = await api.PATCH('/api/campaigns/{id}', {
        params: { path: { id } },
        body: payload,
      });
      return unwrap(result).campaign;
    },
    onSuccess: () => invalidateCampaigns(qc),
  });
};

export const useDeleteCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const result = await api.DELETE('/api/campaigns/{id}', { params: { path: { id } } });
      unwrap(result);
    },
    onSuccess: () => invalidateCampaigns(qc),
  });
};

// Phase 8.5-04 UX-01 — tier-3 hard-delete (ADR-014). Body confirmation must
// equal campaign.name exactly. Server-side: 400 confirmation_mismatch on miss,
// 403 if non-admin, FK CASCADE removes recipients/items/trades.
export const useDestroyCampaign = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; confirmation: string }): Promise<void> => {
      const result = await api.POST('/api/campaigns/{id}/destroy', {
        params: { path: { id: args.id } },
        body: { confirmation: args.confirmation },
      });
      unwrap(result);
    },
    onSuccess: () => invalidateCampaigns(qc),
  });
};

export const useAddCampaignItems = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (items: CampaignItemInput[]): Promise<CampaignItemRecord[]> => {
      const result = await api.POST('/api/campaigns/{id}/items', {
        params: { path: { id } },
        body: { items },
      });
      return unwrap(result).items;
    },
    onSuccess: () => invalidateCampaigns(qc),
  });
};
